import React, { useEffect, useState, useCallback } from "react";
import s from "../styles/Notifications.module.css";

export default function Notifications({ project_id }) {
  const [open, setOpen] = useState(false);

  // для иконки
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [bellActive, setBellActive] = useState(false);

  // для модалки
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // настройки
  const [realEnabled, setRealEnabled] = useState(false);
  const [realThreshold, setRealThreshold] = useState(""); // без дефолтов
  const [avanceEnabled, setAvanceEnabled] = useState(false);
  const [avanceThreshold, setAvanceThreshold] = useState("");

  // 1) Лёгкая загрузка состояния для иконки (минимум данных)
  const fetchSummary = useCallback(async () => {
    if (!project_id) return;
    setSummaryLoading(true);
    try {
      const res = await fetch(`/api/projects/real_avance_notifications?project_id=${encodeURIComponent(project_id)}`, {
        credentials: "include",
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d = await res.json();
      setBellActive(Boolean(d.real_balance_notify_enabled || d.avance_balance_notify_enabled));
    } catch (e) {
      console.error(e);
      // при ошибке не зажигаем, просто молчим
      setBellActive(false);
    } finally {
      setSummaryLoading(false);
    }
  }, [project_id]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // 2) Полная загрузка при открытии модалки
  const openModal = async () => {
    setOpen(true);
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`/api/projects/real_avance_notifications?project_id=${encodeURIComponent(project_id)}`, {
        credentials: "include",
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d = await res.json();
      setRealEnabled(Boolean(d.real_balance_notify_enabled));
      setRealThreshold(String(d.real_balance_threshold ?? ""));      // сервер даёт дефолты — берём их
      setAvanceEnabled(Boolean(d.avance_balance_notify_enabled));
      setAvanceThreshold(formatKopecksToRubString(d.avance_balance_threshold ?? 0));  // сервер даёт дефолты — берём их
    } catch (e) {
      console.error(e);
      setError("Не удалось загрузить настройки.");
    } finally {
      setLoading(false);
    }
  };


    // "12,34" -> 12.34 (>=0), пустое/мусор -> 0
    function parseRubToNumber(str) {
    if (str === null || str === undefined) return 0;
    const n = Number(String(str).replace(",", ".").trim());
    return Number.isFinite(n) && n > 0 ? n : 0;
    }
    // 12345 (коп.) -> "123.45"
    function formatKopecksToRubString(kop) {
    const n = Number(kop);
    if (!Number.isFinite(n) || n <= 0) return "0";
    return (n / 100).toFixed(2).replace(/\.00$/, ""); // убираем .00 для красоты
    }
    // 12.34 (руб) -> 1234 (коп) с безопасным округлением
    function rubToKopecksInt(rub) {
    return Math.round(rub * 100);
    }

  // 3) Сохранение всех настроек разом
  const saveAll = async () => {
    setSaving(true);
    setError("");
    try {
      // нормализация значений перед отправкой
        let real = Number(realThreshold);
        if (!Number.isFinite(real) || real < 0) real = 0;
        
        const avcRub = parseRubToNumber(avanceThreshold);   // рубли на входе из инпута
        let avc = rubToKopecksInt(avcRub);                  // целые копейки
        if (!Number.isFinite(avc) || avc < 0) avc = 0;

      const res = await fetch("/api/projects/real_avance_notifications", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          project_id,
          real_balance_notify_enabled: realEnabled,
          real_balance_threshold: real,
          avance_balance_notify_enabled: avanceEnabled,
          avance_balance_threshold: avc,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d = await res.json();

      // синхронизируемся с сервером
      setRealEnabled(Boolean(d.real_balance_notify_enabled));
      setRealThreshold(String(d.real_balance_threshold ?? ""));
      setAvanceEnabled(Boolean(d.avance_balance_notify_enabled));
      setAvanceThreshold(
            formatKopecksToRubString(d.avance_balance_threshold ?? 0)
            );

      // обновим подсветку колокольчика
      setBellActive(Boolean(d.real_balance_notify_enabled || d.avance_balance_notify_enabled));

      setOpen(false);
    } catch (e) {
      console.error(e);
      setError("Не удалось сохранить. Повторите попытку.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={s.wrapper}>
      <button
        type="button"
        className={`${s.bell} ${bellActive ? s.bellActive : ""}`}
        onClick={openModal}
        title={summaryLoading ? "…" : "Уведомления проекта"}
        disabled={summaryLoading}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 22a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22zm6-6V11a6 6 0 1 0-12 0v5l-2 2v1h16v-1l-2-2z"></path>
        </svg>
      </button>

      {open && (
        <div className={s.modalOverlay} onClick={(e) => e.target === e.currentTarget && setOpen(false)}>
          <div className={s.modal}>
            <div className={s.modalHeader}>
              <h3>Настройка уведомлений в Телеграм</h3>
              <button type="button" className={s.iconBtn} onClick={() => setOpen(false)} aria-label="Закрыть">×</button>
            </div>

            <div className={s.modalBody}>
              {loading ? (
                <div className={s.status}>Загрузка…</div>
              ) : (
                <>
                  <section className={s.block}>
                    
                    <label className={s.label} htmlFor="real-thr">Уведомить при минимуме Кошелька</label>
                    <div className={s.gap_checkbox_div}>
                            <input
                            id="real-thr"
                            className={s.input}
                            type="number"
                            step="0.01"
                            min="0"
                            inputMode="decimal"
                            value={realThreshold}
                            onChange={(e) => setRealThreshold(e.target.value)}
                            disabled={saving}
                            placeholder=""
                            />
                            
                            <div className={s.blockHead}>
                            <label className={s.switch}>
                                <input
                                type="checkbox"
                                checked={realEnabled}
                                onChange={(e) => setRealEnabled(e.target.checked)}
                                disabled={saving}
                                />
                                <span className={s.slider}></span>
                            </label>
                            </div>
                        </div>
                  </section>

                  <section className={s.block}>
                    <label className={s.label} htmlFor="avc-thr">Уведомить при минимуме Аванса</label>
                    <div className={s.gap_checkbox_div}>
                        <input
                        id="avc-thr"
                        className={s.input}
                        type="number"
                        step="0.01"
                        min="0"
                        inputMode="decimal"
                        value={avanceThreshold}
                        onChange={(e) => setAvanceThreshold(e.target.value)}
                        disabled={saving}
                        placeholder=""
                        />
                        <div className={s.blockHead}>
                        <label className={s.switch}>
                            <input
                            type="checkbox"
                            checked={avanceEnabled}
                            onChange={(e) => setAvanceEnabled(e.target.checked)}
                            disabled={saving}
                            />
                            <span className={s.slider}></span>
                        </label>
                        </div>
                    </div>
                  </section>

                  {error && <div className={s.error}>{error}</div>}

                  <div className={s.footer}>
                    <button className={s.btn} onClick={() => setOpen(false)} disabled={saving}>Отмена</button>
                    <button className={s.btnPrimary} onClick={saveAll} disabled={saving}>
                      {saving ? "Сохранение…" : "Сохранить"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
